import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, Grid, Select, MenuItem, FormControl, InputLabel, Chip, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsiveBar } from '@nivo/bar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { EmptyState } from '../../../../components/global';

const BudgetVariance = () => {
  const theme = useTheme();
  const [selectedProduction, setSelectedProduction] = useState(null);

  const { data: productions } = useSWR('/productions');
  const { data, error, isLoading } = useSWR(
    selectedProduction 
      ? `/reports/budget-variance?productionId=${selectedProduction}`
      : '/reports/budget-variance',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Ανάλυση Απόκλισης Προϋπολογισμού" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const variance = data || [];
  const overBudget = variance.filter(v => v.status === 'over');
  const underBudget = variance.filter(v => v.status === 'under');
  const onTrack = variance.filter(v => v.status === 'on-track');

  const chartData = variance.slice(0, 10).map((v) => ({
    item: v.itemName.length > 20 ? v.itemName.substring(0, 20) + '...' : v.itemName,
    budget: v.budget,
    actual: v.actualExpenses,
  }));

  const columns = [
    {
      field: 'productionName',
      headerName: 'Παραγωγή',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'itemName',
      headerName: 'Στοιχείο',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'budget',
      headerName: 'Προϋπολογισμός',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'actualExpenses',
      headerName: 'Πραγματικά',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'varianceAmount',
      headerName: 'Απόκλιση',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        const color = value > 0 ? theme.palette.error.main : theme.palette.success.main;
        return (
          <Typography sx={{ color, fontWeight: 600 }}>
            {formatCurrency(value)}
          </Typography>
        );
      },
    },
    {
      field: 'variancePercent',
      headerName: 'Απόκλιση %',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.value;
        const color = value > 0 ? theme.palette.error.main : theme.palette.success.main;
        return (
          <Typography sx={{ color, fontWeight: 600 }}>
            {value.toFixed(2)}%
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.value;
        const color = status === 'over' ? 'error' : status === 'under' ? 'warning' : 'success';
        const label = status === 'over' ? 'Υπέρ' : status === 'under' ? 'Κάτω' : 'Εντός';
        const icon = status === 'over' ? <ErrorOutlineIcon /> : status === 'under' ? <WarningIcon /> : <CheckCircleOutlineIcon />;
        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      field: 'budgetUtilization',
      headerName: 'Χρήση %',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.value;
        return (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(value, 100)}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: theme.palette.action.hover,
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    value > 100
                      ? theme.palette.error.main
                      : value > 80
                        ? theme.palette.warning.main
                        : theme.palette.success.main,
                },
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              {value.toFixed(1)}%
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Ανάλυση Απόκλισης Προϋπολογισμού" module="Αναφορές" subtitle="Σύγκριση προϋπολογισμού με πραγματικά έξοδα" />

      {/* Production Selector */}
      {productions && productions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>Επιλογή Παραγωγής</InputLabel>
            <Select
              value={selectedProduction ? String(selectedProduction) : ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedProduction(value === '' ? null : parseInt(value));
              }}
              label="Επιλογή Παραγωγής"
            >
              <MenuItem value="">Όλες οι Παραγωγές</MenuItem>
              {productions.map((prod) => (
                <MenuItem key={prod.id} value={String(prod.id)}>
                  {prod.p_descr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Σύνολο Στοιχείων
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {variance.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3], border: `2px solid ${theme.palette.error.main}` }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Υπέρ Προϋπολογισμού
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
              {overBudget.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3], border: `2px solid ${theme.palette.warning.main}` }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Κάτω Προϋπολογισμού
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
              {underBudget.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3], border: `2px solid ${theme.palette.success.main}` }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Εντός Προϋπολογισμού
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
              {onTrack.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart */}
      {chartData.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, height: 400, boxShadow: theme.shadows[3] }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Προϋπολογισμός vs Πραγματικά (Top 10)
          </Typography>
          <Box sx={{ height: 320 }}>
            <ResponsiveBar
              data={chartData}
              keys={['budget', 'actual']}
              indexBy="item"
              margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
              padding={0.3}
              valueFormat={formatCurrency}
              colors={[theme.palette.success.main, theme.palette.info.main]}
              axisLeft={{
                format: (value) => formatCurrency(value),
              }}
              axisBottom={{
                tickRotation: -45,
              }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  translateX: 120,
                  itemWidth: 80,
                  itemHeight: 20,
                },
              ]}
              theme={{
                text: {
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                },
                grid: {
                  line: {
                    stroke: theme.palette.divider,
                  },
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
        <FormListToolbar disableAdd />
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={variance.map((v, index) => ({ ...v, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default BudgetVariance;

